import { ReactElement } from 'react'
import { Button, Icon } from '@dynamo-caffeina-dev/airc-ui-kit'
import { PaginationType } from '@/types/PaginationType'
import { formatDate } from '@/helpers/date'

interface PaginationProps {
  meta: PaginationType
  onPageChange: (page: number) => void
  className?: string
}

export default function Pagination({
  meta,
  onPageChange,
  className = '',
}: PaginationProps): ReactElement | null {
  const { current_page, last_page, total, from, to } = meta

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxPagesToShow = 5
    const halfRange = Math.floor(maxPagesToShow / 2)

    let startPage = Math.max(1, current_page - halfRange)
    let endPage = Math.min(last_page, current_page + halfRange)

    if (endPage - startPage + 1 < maxPagesToShow) {
      if (startPage === 1) {
        endPage = Math.min(last_page, startPage + maxPagesToShow - 1)
      } else {
        startPage = Math.max(1, endPage - maxPagesToShow + 1)
      }
    }

    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) {
        pages.push('...')
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    if (endPage < last_page) {
      if (endPage < last_page - 1) {
        pages.push('...')
      }
      pages.push(last_page)
    }

    return pages
  }

  if (last_page <= 1) {
    return null
  }

  return (
    <div className={`pagination ${className}`}>
      {typeof from === 'string' && typeof to === 'string' ? (
        <div className='pagination__info'>
          <strong>{total}</strong> risultati nel periodo dal{' '}
          <strong>{formatDate(from)}</strong> al{' '}
          <strong>{formatDate(to)}</strong>
        </div>
      ) : (
        <div className='pagination__info'>
          Risultati <strong>{from}</strong> &mdash; <strong>{to}</strong> di{' '}
          <strong>{total}</strong>
        </div>
      )}

      <nav
        className='pagination__nav'
        aria-label='Navigazione pagine'
      >
        <Button
          animate={false}
          size={'small'}
          variant='negative'
          className='pagination__button pagination__button--prev'
          onClick={() => onPageChange(current_page - 1)}
          disabled={current_page === 1}
          aria-label='Pagina precedente'
        >
          <Icon icon={'ArrowLeft'} />{' '}
          <span className='pagination__button__label'>Precedente</span>
        </Button>

        <div className='pagination__pages'>
          {getPageNumbers().map((page, index) => {
            const isCurrentPage = page === current_page

            return typeof page === 'number' ? (
              <Button
                key={index}
                size={'small'}
                variant={isCurrentPage ? 'themed' : 'negative'}
                className={`pagination__page`}
                data-active={isCurrentPage}
                onClick={() => onPageChange(page)}
                aria-label={`Vai alla pagina ${page}`}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {page}
              </Button>
            ) : (
              <span
                className='pagination__ellipsis'
                key={index}
              >
                {page}
              </span>
            )
          })}
        </div>

        <Button
          animate={false}
          size={'small'}
          variant='negative'
          className='pagination__button pagination__button--next'
          onClick={() => onPageChange(current_page + 1)}
          disabled={current_page === last_page}
          aria-label='Pagina successiva'
        >
          <span className='pagination__button__label'>Successiva</span>{' '}
          <Icon icon={'ArrowRight'} />
        </Button>
      </nav>
    </div>
  )
}
